import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { EmailTemplateId, UserId, validEmail } from "@sp-crm/core";
import {
    AdvancedSearchEntityType,
    GetWorkflowSettingsResult,
    useGetTaskTypesQuery,
    useGetTemplateQuery,
    WorkflowActorMode,
} from "generated/graphql";
import { useTaskNouns } from "hooks/use-task-nouns";
import React, { useMemo, useState } from "react";
import { useUser } from "store/selectors/hooks";
import { getSanitizedMarkUpFromEmailBody } from "util/email";
import { stableQueryOptions } from "util/requests";
import {
    entityTypeNoun,
    getEmailRecipientTokens,
    resolveWorkflowSetting,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowActionSummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

interface ExpandableActionSummaryProps {
    summaryText: string;
    children: React.ReactNode;
}

const ExpandableActionSummary: React.FC<ExpandableActionSummaryProps> = ({
    summaryText,
    children,
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <span
                className="cursor-pointer inline-flex items-center gap-1"
                onClick={() => setExpanded(!expanded)}>
                {summaryText}
                {expanded ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500" />
                ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                )}
            </span>
            {expanded && (
                <dl className="mt-1 ml-4 text-gray-600 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                    {children}
                </dl>
            )}
        </>
    );
};

const UserName: React.FC<{ userId: UserId | null | undefined }> = ({ userId }) => {
    const user = useUser(userId);

    if (!user) {
        return <span>a specific user</span>;
    }

    return <span>{user.name}</span>;
};

const resolveRecipientDescription = (
    recipientValue: string | null,
    attachTo: AdvancedSearchEntityType,
): string => {
    if (!recipientValue) {
        return "configured recipients";
    }

    const recipientTokens = [
        ...getEmailRecipientTokens(attachTo),
        ...(attachTo === AdvancedSearchEntityType.Client
            ? [{ label: "Referral contact", token: "{{client.referralContact}}" }]
            : []),
    ];

    const recipients = recipientValue.split(",").map(r => r.trim());

    const labels: string[] = [];
    const literalEmails: string[] = [];

    for (const recipient of recipients) {
        const tokenMatch = recipientTokens.find(t => t.token === recipient);
        if (tokenMatch) {
            labels.push(tokenMatch.label.toLowerCase());
        } else if (validEmail(recipient)) {
            literalEmails.push(recipient);
        }
    }

    const allLabels = [...labels, ...literalEmails];
    if (allLabels.length === 0) {
        return "Configured recipients";
    }

    const result = allLabels.join(", ");
    return result.charAt(0).toUpperCase() + result.slice(1);
};

type WorkflowAction = EditableWorkflow["actions"][number];

interface EmailActionSummaryProps {
    workflow: EditableWorkflow;
    action: WorkflowAction;
    workflowSettings: GetWorkflowSettingsResult;
}

const EmailActionSummary: React.FC<EmailActionSummaryProps> = ({
    workflow,
    action,
    workflowSettings,
}) => {
    const isAssignedUser = workflow.actorMode === WorkflowActorMode.AssignedUser;

    const templateId = useMemo(() => {
        return resolveWorkflowSetting(
            workflowSettings,
            workflow,
            "emailTemplate",
        ) as EmailTemplateId | null;
    }, [workflowSettings, workflow]);

    const getTemplate = useGetTemplateQuery(
        { id: templateId },
        { enabled: !!templateId },
    );

    const recipientValue = resolveWorkflowSetting(
        workflowSettings,
        workflow,
        "recipients",
    );

    const discreteToDescriptions: Record<string, string> = {
        referredUnselectedCommunities: "Each unchosen community that received a referral",
        allCommunityOptions: "All community options",
    };

    let recipientDescription: string;
    if (
        action?.__typename === "WorkflowActionEmailEntity" &&
        action.discreteTo &&
        discreteToDescriptions[action.discreteTo]
    ) {
        recipientDescription = discreteToDescriptions[action.discreteTo];
    } else if (recipientValue) {
        recipientDescription = resolveRecipientDescription(
            recipientValue,
            workflow.attachTo,
        );
    } else if (
        action?.__typename === "WorkflowActionEmailEntity" &&
        action.to === "{{client.referralContact}}"
    ) {
        recipientDescription = "Referral contact";
    } else {
        recipientDescription = "Configured recipients";
    }

    const templateSubject = getTemplate.data?.getTemplate?.template?.subject;
    const templateBody = getTemplate.data?.getTemplate?.template?.body;

    return (
        <ExpandableActionSummary
            summaryText={`Send email to ${recipientDescription.toLowerCase()}`}>
            <dt className="text-gray-500">To</dt>
            <dd>{recipientDescription}</dd>
            <dt className="text-gray-500">From</dt>
            <dd>
                {isAssignedUser ? (
                    `The ${entityTypeNoun(workflow.attachTo)}'s assigned user`
                ) : (
                    <UserName userId={workflow.defaultActorId} />
                )}
            </dd>
            {templateSubject && (
                <>
                    <dt className="text-gray-500">Subject</dt>
                    <dd>{templateSubject}</dd>
                </>
            )}
            {templateBody ? (
                <>
                    <dt className="text-gray-500">Body</dt>
                    <dd>
                        <div
                            className="unreset rounded border border-gray-200 p-2 text-sm"
                            dangerouslySetInnerHTML={getSanitizedMarkUpFromEmailBody(
                                templateBody,
                            )}
                        />
                    </dd>
                </>
            ) : null}
        </ExpandableActionSummary>
    );
};

interface TaskActionSummaryProps {
    workflow: EditableWorkflow;
    action: WorkflowAction;
    workflowSettings: GetWorkflowSettingsResult;
}

const TaskActionSummary: React.FC<TaskActionSummaryProps> = ({
    workflow,
    action,
    workflowSettings,
}) => {
    const nouns = useTaskNouns();
    const taskTypes = useGetTaskTypesQuery({}, stableQueryOptions());

    const taskTypeId =
        action?.__typename === "WorkflowActionTaskEntity" ? action.taskTypeId : null;

    const taskType = taskTypes.data?.getTaskTypes?.find(t => t.id === taskTypeId);

    const description = resolveWorkflowSetting(workflowSettings, workflow, "description");
    const cleanDescription = description?.trim() || null;

    const isAssignedUser = workflow.actorMode === WorkflowActorMode.AssignedUser;

    const summaryText = taskType?.name || cleanDescription || nouns.singular;

    return (
        <ExpandableActionSummary summaryText={`Create ${nouns.singular}: ${summaryText}`}>
            {taskType && (
                <>
                    <dt className="text-gray-500">Type</dt>
                    <dd>{taskType.name}</dd>
                </>
            )}
            {cleanDescription && (
                <>
                    <dt className="text-gray-500">Description</dt>
                    <dd>{cleanDescription}</dd>
                </>
            )}
            <dt className="text-gray-500">Assigned to</dt>
            <dd>
                {isAssignedUser ? (
                    `The ${entityTypeNoun(workflow.attachTo)}'s assigned user`
                ) : (
                    <UserName userId={workflow.defaultActorId} />
                )}
            </dd>
        </ExpandableActionSummary>
    );
};

export const WorkflowActionSummary: React.FC<WorkflowActionSummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const actions = workflow.actions ?? [];

    if (actions.length === 0) {
        return null;
    }

    return (
        <div className="space-y-1">
            {actions.map((action, index) =>
                action.__typename === "WorkflowActionEmailEntity" ? (
                    <div key={index}>
                        <EmailActionSummary
                            workflow={workflow}
                            action={action}
                            workflowSettings={workflowSettings}
                        />
                    </div>
                ) : (
                    <div key={index}>
                        <TaskActionSummary
                            workflow={workflow}
                            action={action}
                            workflowSettings={workflowSettings}
                        />
                    </div>
                ),
            )}
        </div>
    );
};
